// Add-member form inside the team modal. Kept separate so the
// parent modal stays under the 150-line cap.
import { useState } from "react";
import type { TeamMemberInputT } from "../../../../../../shared/types/production-schedule";

interface Props {
  onAdd(input: TeamMemberInputT): Promise<void>;
}

const COLOURS: { value: string; label: string }[] = [
  { value: "#1B449C", label: "BSP Blue" },
  { value: "#0284C7", label: "Sky Blue" },
  { value: "#0f766e", label: "Teal" },
  { value: "#7C3AED", label: "Purple" },
  { value: "#C77700", label: "BSP Orange" },
  { value: "#2E7D32", label: "BSP Green" },
  { value: "#be185d", label: "Pink" },
  { value: "#92400e", label: "Brown" },
  { value: "#707372", label: "BSP Grey" },
];

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

export function TeamModalForm({ onAdd }: Props) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [initials, setInitials] = useState("");
  const [color, setColor] = useState(COLOURS[0]!.value);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!name.trim() || !role.trim()) {
      setError("Name and role are required.");
      return;
    }
    setSubmitting(true);
    try {
      await onAdd({
        name: name.trim(),
        role: role.trim(),
        initials: (initials.trim() || deriveInitials(name)).toUpperCase(),
        color,
        sortOrder: 0,
        active: true,
      });
      setName("");
      setRole("");
      setInitials("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: "var(--bsp-surface-muted)", borderRadius: 4, padding: 14, border: "1px dashed var(--bsp-border-strong)" }}>
      <div style={{ fontFamily: "var(--bsp-font-display)", fontSize: 11, fontWeight: 700, color: "var(--bsp-blue)", textTransform: "uppercase", letterSpacing: "0.10em", marginBottom: 10 }}>
        Add New Member
      </div>
      {error && <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>{error}</div>}
      <div className="form-grid">
        <div className="form-group">
          <label>Full Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sarah Johnson" />
        </div>
        <div className="form-group">
          <label>Role</label>
          <input type="text" value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Welder" />
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Initials</label>
          <input type="text" value={initials} onChange={(e) => setInitials(e.target.value)} placeholder="SJ" maxLength={3} />
        </div>
        <div className="form-group">
          <label>Avatar Colour</label>
          <select value={color} onChange={(e) => setColor(e.target.value)}>
            {COLOURS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" className="btn-submit" onClick={submit} disabled={submitting}>
          {submitting ? "Adding…" : "Add Member"}
        </button>
      </div>
    </div>
  );
}
